import React, { useMemo, useState } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Trash2, Home, Wrench } from "lucide-react";
import { ALL_MODELS } from "../../lib/wizardCalc";
import { unitLabel, fmtKind } from "../../lib/units";
import NumberInput from "./NumberInput";
import { QtyInput } from "./QtyInput";
import { toast } from "sonner";

const blankRoom = (lastModel) => ({
  id: crypto.randomUUID ? crypto.randomUUID() : `r_${Date.now()}_${Math.random()}`,
  name: "",
  qty: 1,
  cooling_total_btuh: "",
  cooling_sensible_btuh: "",
  cooling_latent_btuh: "",  // derived
  heating_btuh: "",
  oa_flow_cfm: "",
  ea_flow_cfm: "",
  unit_model: lastModel || "",
  notes: "",
});

export default function RoomDetailsTab({ rooms, setRooms, isSI = false, hideCapacityColumns = false, onRemoveRoom, onBulkDeleteRooms, updateRoomQty, results }) {
  // Bulk-selection state: a Set of room ids. Stored as Set for O(1) toggle.
  const [selected, setSelected] = useState(() => new Set());
  // When the user picks a model from the bulk-edit dropdown we apply it to
  // every selected row in one go. Setting a "—" value is a no-op.
  const [bulkModel, setBulkModel] = useState("");

  // Drop ids from `selected` whenever the underlying rooms list shrinks (e.g.
  // after delete or after an import replaces the entire list).
  React.useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const valid = new Set(rooms.map((r) => r.id));
      const next = new Set([...prev].filter((id) => valid.has(id)));
      // Avoid no-op state churn that would re-trigger this effect.
      if (next.size === prev.size) return prev;
      return next;
    });
  }, [rooms]);

  const allChecked = rooms.length > 0 && selected.size === rooms.length;
  const someChecked = selected.size > 0 && selected.size < rooms.length;

  const toggleAll = () => {
    setSelected(allChecked ? new Set() : new Set(rooms.map((r) => r.id)));
  };
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const removeRoom = (id) => {
    if (rooms.length === 1) return;
    const idx = rooms.findIndex((r) => r.id === id);
    if (idx < 0) return;
    if (onRemoveRoom) {
      onRemoveRoom(idx);
    } else {
      setRooms(rooms.filter((r) => r.id !== id));
    }
  };

  // Bulk delete — refuse to leave the project with zero rooms (Step 2 requires
  // at least one row). The user can still individually edit / re-import.
  const bulkDelete = () => {
    if (selected.size === 0) return;
    if (selected.size >= rooms.length) {
      toast.error("At least one room must remain. Clear some checkboxes first or re-import a fresh template.");
      return;
    }
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Delete ${selected.size} selected room${selected.size === 1 ? "" : "s"}?`)) return;
    const deletedIndices = rooms
      .map((r, i) => ({ id: r.id, i }))
      .filter(({ id }) => selected.has(id))
      .map(({ i }) => i);
    if (onBulkDeleteRooms) {
      onBulkDeleteRooms(deletedIndices);
    } else {
      const keep = rooms.filter((r) => !selected.has(r.id));
      setRooms(keep);
    }
    setSelected(new Set());
    toast.success(`Deleted ${selected.size} room(s)`);
  };

  // Copy the first room's unit_model into every other row. EA is cleared on
  // non-Model-N rows (matches per-row + bulk-set semantics). No-op when
  // there's nothing to copy.
  const fillModelFromFirst = () => {
    if (!rooms || rooms.length < 2) {
      toast.info("Need at least 2 rows to fill down from row 1.");
      return;
    }
    const first = rooms[0]?.unit_model;
    if (!first) {
      toast.error("Set a Unit Model on row 1 first.");
      return;
    }
    setRooms(rooms.map((r, i) => {
      if (i === 0) return r;
      const next = { ...r, unit_model: first };
      if (first !== "Model N" && first !== "Model N-f") next.ea_flow_cfm = "";
      return next;
    }));
    toast.success(`Filled "${first}" across ${rooms.length - 1} room(s).`);
  };

  // Bulk apply unit_model to every selected row. EA is cleared on non-Model-N
  // models to mirror the per-row updateRoom logic.
  const bulkSetModel = (model) => {
    if (!model || selected.size === 0) {
      setBulkModel(model || "");
      return;
    }
    setRooms(rooms.map((r) => {
      if (!selected.has(r.id)) return r;
      const next = { ...r, unit_model: model };
      if (model !== "Model N" && model !== "Model N-f") next.ea_flow_cfm = "";
      return next;
    }));
    setBulkModel("");
    toast.success(`Set unit model to "${model}" on ${selected.size} room(s)`);
  };

  // SI-aware input wiring is delegated to NumberInput: it edits a local draft
  // while focused and only commits an IP-string back to room state on blur.
  // This makes typing 1000s + decimals robust regardless of unit system.
  const KIND_BY_KEY = {
    cooling_total_btuh: "capacity",
    cooling_sensible_btuh: "capacity",
    heating_btuh: "capacity",
    oa_flow_cfm: "flow",
    ea_flow_cfm: "flow",
  };

  const updateRoom = (id, key, value) => {
    setRooms(
      rooms.map((r) => {
        if (r.id !== id) return r;
        const next = { ...r, [key]: value };
        // Auto-derive latent = total − sensible
        if (key === "cooling_total_btuh" || key === "cooling_sensible_btuh") {
          const total = parseFloat(next.cooling_total_btuh);
          const sens = parseFloat(next.cooling_sensible_btuh);
          if (!isNaN(total) && !isNaN(sens) && total >= sens) {
            next.cooling_latent_btuh = (total - sens).toFixed(0);
          }
        }
        // EA only applies to Model N / N-f. When user switches to another model,
        // clear the EA value so it isn't carried into calc/PDF as a phantom input.
        if (key === "unit_model" && value !== "Model N" && value !== "Model N-f") {
          next.ea_flow_cfm = "";
        }
        return next;
      })
    );
  };

  return (
    <Card data-testid="wizard-rooms-tab">
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-lg">
          <span className="flex items-center gap-2">
            <Home className="h-5 w-5 text-muted-foreground" />
            Room Details
          </span>
          <span className="text-sm font-normal text-muted-foreground">
            {rooms.length} room{rooms.length === 1 ? "" : "s"}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {/* Bulk-action toolbar — visible only when 1+ rooms are selected AND not in single-unit mode. */}
        {!hideCapacityColumns && selected.size > 0 && (
          <div
            className="flex flex-wrap items-center gap-3 mb-3 p-3 bg-muted border border-border rounded-md"
            data-testid="wizard-rooms-bulk-toolbar"
          >
            <span className="text-sm font-medium text-foreground">
              {selected.size} room{selected.size === 1 ? "" : "s"} selected
            </span>
            <div className="flex items-center gap-2">
              <Wrench className="h-4 w-4 text-muted-foreground" />
              <Label className="text-xs text-muted-foreground">Set Unit Model:</Label>
              <Select value={bulkModel} onValueChange={bulkSetModel}>
                <SelectTrigger
                  className="h-8 text-xs w-40"
                  data-testid="wizard-rooms-bulk-model"
                >
                  <SelectValue placeholder="Pick a model…" />
                </SelectTrigger>
                <SelectContent>
                  {ALL_MODELS.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1" />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelected(new Set())}
              data-testid="wizard-rooms-bulk-clear"
            >
              Clear
            </Button>
            <Button
              size="sm"
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={bulkDelete}
              data-testid="wizard-rooms-bulk-delete"
            >
              <Trash2 className="h-4 w-4 mr-1" /> Delete Selected
            </Button>
          </div>
        )}
        <Table className="text-xs">
          <TableHeader>
            <TableRow>
              {!hideCapacityColumns && (
                <TableHead className="w-10">
                  <Checkbox
                    checked={allChecked ? true : (someChecked ? "indeterminate" : false)}
                    onCheckedChange={toggleAll}
                    data-testid="wizard-rooms-select-all"
                    aria-label="Select all rooms"
                  />
                </TableHead>
              )}
              <TableHead className="w-12">No</TableHead>
              <TableHead className="min-w-[140px]">Room Name</TableHead>
              <TableHead className="w-16 text-center">QTY</TableHead>
              {!hideCapacityColumns && <TableHead className="min-w-[112px]">Total ({unitLabel("capacity", isSI)})</TableHead>}
              {!hideCapacityColumns && <TableHead className="min-w-[112px]">Sensible ({unitLabel("capacity", isSI)})</TableHead>}
              {!hideCapacityColumns && <TableHead className="text-muted-foreground">Latent ({unitLabel("capacity", isSI)})</TableHead>}
              {!hideCapacityColumns && <TableHead className="min-w-[112px]">Heating ({unitLabel("capacity", isSI)})</TableHead>}
              {!hideCapacityColumns && <TableHead>OA ({unitLabel("flow", isSI)})</TableHead>}
              {!hideCapacityColumns && <TableHead>EA ({unitLabel("flow", isSI)})</TableHead>}
              <TableHead className="min-w-[160px]">
                <div className="flex items-center justify-between gap-2">
                  <span>Unit Model</span>
                  {!hideCapacityColumns && (
                    <button
                      type="button"
                      onClick={fillModelFromFirst}
                      disabled={!rooms?.[0]?.unit_model || rooms.length < 2}
                      data-testid="wizard-room-fill-model"
                      title="Copy row 1's Unit Model into every other row"
                      className="text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    >
                      Fill all
                    </button>
                  )}
                </div>
              </TableHead>
              {!hideCapacityColumns && <TableHead className="w-12"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rooms.map((r, idx) => (
              <TableRow
                key={r.id}
                data-testid={`wizard-room-row-${idx}`}
                className={selected.has(r.id) ? "bg-muted" : undefined}
              >
                {!hideCapacityColumns && (
                  <TableCell>
                    <Checkbox
                      checked={selected.has(r.id)}
                      onCheckedChange={() => toggleOne(r.id)}
                      data-testid={`wizard-room-select-${idx}`}
                      aria-label={`Select row ${idx + 1}`}
                    />
                  </TableCell>
                )}
                <TableCell className="font-mono text-muted-foreground">
                  {idx + 1}
                </TableCell>
                <TableCell>
                  <Input
                    data-testid={`wizard-room-name-${idx}`}
                    value={r.name}
                    onChange={(e) => updateRoom(r.id, "name", e.target.value)}
                    placeholder={`Room ${idx + 1}`}
                    className="h-8 text-xs"
                  />
                </TableCell>
                <TableCell className="text-center">
                  <QtyInput
                    value={r.qty ?? 1}
                    onCommit={(v) => {
                      const q = Math.max(1, v);
                      if (updateRoomQty && results?.[idx]?.ok) {
                        updateRoomQty(idx, q, "duplicate");
                      } else {
                        updateRoom(r.id, "qty", q);
                      }
                    }}
                    testid={`wizard-room-qty-${idx}`}
                    className="h-7 w-14 text-xs text-center px-1"
                  />
                </TableCell>
                {!hideCapacityColumns && (
                  <TableCell>
                    <NumberInput
                      data-testid={`wizard-room-total-${idx}`}
                      value={r.cooling_total_btuh}
                      onChange={(v) => updateRoom(r.id, "cooling_total_btuh", v)}
                      isSI={isSI}
                      kind="capacity"
                      decimals={0}
                      className="h-8 text-xs min-w-[112px]"
                    />
                  </TableCell>
                )}
                {!hideCapacityColumns && (
                  <TableCell>
                    <NumberInput
                      data-testid={`wizard-room-sens-${idx}`}
                      value={r.cooling_sensible_btuh}
                      onChange={(v) => updateRoom(r.id, "cooling_sensible_btuh", v)}
                      isSI={isSI}
                      kind="capacity"
                      decimals={0}
                      className="h-8 text-xs min-w-[112px]"
                    />
                  </TableCell>
                )}
                {!hideCapacityColumns && (
                  <TableCell className="text-muted-foreground font-mono">
                    {r.cooling_latent_btuh ? fmtKind(r.cooling_latent_btuh, "capacity", isSI) : "—"}
                  </TableCell>
                )}
                {!hideCapacityColumns && (
                  <TableCell>
                    <NumberInput
                      data-testid={`wizard-room-heat-${idx}`}
                      value={r.heating_btuh}
                      onChange={(v) => updateRoom(r.id, "heating_btuh", v)}
                      isSI={isSI}
                      kind="capacity"
                      decimals={0}
                      className="h-8 text-xs min-w-[112px]"
                    />
                  </TableCell>
                )}
                {!hideCapacityColumns && (
                  <TableCell>
                    <NumberInput
                      data-testid={`wizard-room-oa-${idx}`}
                      value={r.oa_flow_cfm}
                      onChange={(v) => updateRoom(r.id, "oa_flow_cfm", v)}
                      isSI={isSI}
                      kind="flow"
                      decimals={1}
                      className="h-8 text-xs"
                    />
                  </TableCell>
                )}
                {!hideCapacityColumns && (
                  <TableCell>
                    {(r.unit_model === "Model N" || r.unit_model === "Model N-f") ? (
                      <NumberInput
                        data-testid={`wizard-room-ea-${idx}`}
                        value={r.ea_flow_cfm}
                        onChange={(v) => updateRoom(r.id, "ea_flow_cfm", v)}
                        isSI={isSI}
                        kind="flow"
                        decimals={1}
                        className="h-8 text-xs"
                      />
                    ) : (
                      <span className="text-xs text-muted-foreground/60" title="EA only applies to Model N / N-f">—</span>
                    )}
                  </TableCell>
                )}
                <TableCell>
                  <Select
                    value={r.unit_model || ""}
                    onValueChange={(v) => updateRoom(r.id, "unit_model", v)}
                  >
                    <SelectTrigger
                      className="h-8 text-xs"
                      data-testid={`wizard-room-model-${idx}`}
                    >
                      <SelectValue placeholder="Select model" />
                    </SelectTrigger>
                    <SelectContent>
                      {ALL_MODELS.map((m) => (
                        <SelectItem key={m} value={m}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                {!hideCapacityColumns && (
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => removeRoom(r.id)}
                      disabled={rooms.length === 1}
                      data-testid={`wizard-room-remove-${idx}`}
                    >
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export { blankRoom };
